// Which institution — if any — is this page being served for?
// (institution-prd.md D-INST-11.)
//
// An institution's students sign in at `{short_code}.airbotix.ai`. The short code
// is derived from the HOSTNAME, never typed: making a 7-year-old copy a school
// code off a card was the whole problem with the old three-tab login.

/**
 * The domain institution subdomains hang off. Not configurable at runtime: it is
 * half of the trust decision below, so it must not be something a page can be
 * talked into changing.
 */
const INSTITUTION_DOMAIN = 'airbotix.ai';

/**
 * Subdomains of `airbotix.ai` that are Airbotix's OWN surfaces (or plumbing) and
 * can never be an institution, no matter what an `Institution.short_code` says.
 *
 * This list is load-bearing, not defensive tidiness: without it,
 * `app.airbotix.ai` would resolve to the institution `APP` and the normal family
 * login would try to render as somebody's branded school page. Any new Airbotix
 * hostname must be added here BEFORE it goes live.
 *
 * The backend enforces the same list. Keep this client-side copy aligned so a
 * shared portal host is never briefly rendered as a branded school.
 */
const RESERVED_LABELS = new Set([
  'www',
  'app',
  'api',
  'teacher',
  'admin',
  'learn',
  'portal',
  'play',
  'docs',
  'blog',
  'mail',
  'cdn',
  'static',
  'assets',
  'media',
  'download',
  'downloads',
  'status',
  'staging',
  'staging-app',
  'staging-api',
  'staging-admin',
  'staging-teacher',
  'stage',
  'dev',
  'test',
  'preview',
  'harness',
  'institution',
  'schools',
  'partners',
  'staging-partners',
]);

/**
 * A short code is one DNS label: letters, digits, hyphens, no leading/trailing
 * hyphen. Minimum two characters, matching the backend's
 * `CreateInstitutionSchema` (`short_code: min(2).max(20)`).
 */
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])$/;

/** localhost-equivalents plus the private-LAN ranges (mirrors devHost.ts). */
const LAN_HOST =
  /^(?:10\.\d{1,3}\.\d{1,3}\.\d{1,3}|127\.\d{1,3}\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|172\.(?:1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3})$/;

/**
 * Hosts where a developer override is honoured.
 *
 * This is the ENTIRE gate that keeps the override out of production. It is
 * deliberately a hostname test and nothing else — not an env flag, not
 * `import.meta.env.DEV`, not a build mode — because a hostname is the one thing
 * a visitor to `bright.airbotix.ai` cannot change about the page they loaded.
 * `app.airbotix.ai` can never pass this, so `?school=SOMEONEELSE` can never
 * impersonate an institution in production.
 */
export function isDevHost(hostname: string): boolean {
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') return true;
  if (hostname.endsWith('.localhost') || hostname.endsWith('.local')) return true;
  // Private-LAN ranges, so testing from a phone/tablet on the dev machine's
  // network behaves like localhost (mirrors `sameHostInDev` in devHost.ts).
  return LAN_HOST.test(hostname);
}

/**
 * The institution short code this page is branded for, or null for Airbotix's
 * own surfaces.
 *
 * PRODUCTION PATH — host-derived only: `bright.airbotix.ai` → `'BRIGHT'`. The
 * apex, a reserved label, a deeper subdomain and any other domain all return
 * null.
 *
 * DEV PATH — `?school=bright` on a dev host, or `VITE_INSTITUTION_SHORT_CODE`
 * for a dev server pinned to one school. Both are consulted ONLY when the
 * hostname is a dev host AND the host itself resolved nothing, so:
 *   - the override can never be used in production (see `isDevHost`), and
 *   - it can never override a real subdomain even in dev — the host always wins,
 *     so a link like `bright.airbotix.ai/learn/login?school=OTHER` still renders
 *     BRIGHT.
 */
export function resolveInstitutionShortCode(
  hostname: string,
  search = '',
  envShortCode: string | undefined = import.meta.env.VITE_INSTITUTION_SHORT_CODE as
    | string
    | undefined,
): string | null {
  const host = hostname.trim().toLowerCase().replace(/\.$/, '');

  const suffix = `.${INSTITUTION_DOMAIN}`;
  if (host.endsWith(suffix)) {
    const label = host.slice(0, -suffix.length);
    // One label only. `evil.attacker.airbotix.ai` must not read as an
    // institution, and neither must the apex (empty label).
    if (LABEL.test(label) && !label.includes('.') && !RESERVED_LABELS.has(label)) {
      return label.toUpperCase();
    }
    return null;
  }

  if (!isDevHost(host)) return null;

  const fromQuery = new URLSearchParams(search).get('school');
  const candidate = (fromQuery ?? envShortCode ?? '').trim().toLowerCase();
  if (!candidate || !LABEL.test(candidate)) return null;
  return candidate.toUpperCase();
}

/** Convenience wrapper reading the live browser location. Null when there is none (SSR/tests). */
export function currentInstitutionShortCode(): string | null {
  if (typeof window === 'undefined') return null;
  return resolveInstitutionShortCode(window.location.hostname, window.location.search);
}
